import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// Starts after the welcome modal's 1s delay, so the welcome gets the first say.
const REPORT_SEASON_DELAY_MS = 1500;

/**
 * One-off "it's report season" announcement for existing users, shown once per user
 * (per browser, like the welcome modal). Brand-new users get the welcome modal instead
 * and are marked as seen so they aren't pitched the reports overhaul on their next visit.
 */
export const useReportSeasonModal = (isWelcomeModalOpen: boolean) => {
  const { isAuthenticated, user } = useAuth0();
  const [isReportSeasonOpen, setIsReportSeasonOpen] = useState(false);
  const sub = user?.sub;

  useEffect(() => {
    if (!isAuthenticated || !sub) {
      return;
    }

    const key = `reportSeasonShown_${sub}`;
    if (isWelcomeModalOpen) {
      localStorage.setItem(key, 'true');
      // Both timers can fire in the same tick on a busy main thread; the welcome wins.
      setIsReportSeasonOpen(false);
      return;
    }

    // A deep link (e.g. /dashboard#reports after signing in from a preview) already
    // took the user somewhere; don't cover it, try again on a later visit.
    if (localStorage.getItem(key) === 'true' || window.location.hash) {
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem(key, 'true');
      setIsReportSeasonOpen(true);
    }, REPORT_SEASON_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isAuthenticated, sub, isWelcomeModalOpen]);

  const closeReportSeasonModal = () => setIsReportSeasonOpen(false);

  return { isReportSeasonOpen, closeReportSeasonModal };
};
